/**
 * Directional Movement Index (DMI)
 *
 * Shows +DI and −DI for directional movement analysis.
 * Similar to ADX but focused on direction rather than strength.
 *
 * Category: Momentum | Pane: Separate
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";

export const dmiIndicator: IndicatorDefinition = {
  id: "dmi",
  name: "Directional Movement Index",
  shortName: "DMI",
  category: "momentum",
  pane: "separate",
  params: [
    { key: "period", label: "Period", type: "number", default: 14, min: 2, max: 200 },
  ],
  plots: [
    { key: "plusDI", label: "+DI", color: "#4CAF50", style: "line", lineWidth: 2, priceScaleId: "dmi" },
    { key: "minusDI", label: "-DI", color: "#F44336", style: "line", lineWidth: 2, priceScaleId: "dmi" },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { period } = params;
    if (candles.length < period + 1)
      return [{ key: "plusDI", data: [] }, { key: "minusDI", data: [] }];

    const tr: number[] = [];
    const plusDM: number[] = [];
    const minusDM: number[] = [];

    for (let i = 1; i < candles.length; i++) {
      const hl = candles[i].high - candles[i].low;
      const hc = Math.abs(candles[i].high - candles[i - 1].close);
      const lc = Math.abs(candles[i].low - candles[i - 1].close);
      tr.push(Math.max(hl, hc, lc));

      const upMove = candles[i].high - candles[i - 1].high;
      const downMove = candles[i - 1].low - candles[i].low;
      plusDM.push(upMove > downMove && upMove > 0 ? upMove : 0);
      minusDM.push(downMove > upMove && downMove > 0 ? downMove : 0);
    }

    // Wilder smooth
    const smooth = (arr: number[]): number[] => {
      const r: number[] = [];
      let s = 0;
      for (let i = 0; i < arr.length; i++) {
        if (i < period) { s += arr[i]; r.push(i === period - 1 ? s : NaN); }
        else { const prev = r[r.length - 1]; r.push(prev - prev / period + arr[i]); }
      }
      return r;
    };

    const sTR = smooth(tr);
    const sPDM = smooth(plusDM);
    const sMDM = smooth(minusDM);

    const pOut: { time: number; value: number }[] = [];
    const mOut: { time: number; value: number }[] = [];

    for (let i = 0; i < sTR.length; i++) {
      if (isNaN(sTR[i]) || sTR[i] === 0) continue;
      pOut.push({ time: candles[i + 1].time, value: (sPDM[i] / sTR[i]) * 100 });
      mOut.push({ time: candles[i + 1].time, value: (sMDM[i] / sTR[i]) * 100 });
    }

    return [{ key: "plusDI", data: pOut }, { key: "minusDI", data: mOut }];
  },
};
